import Phaser from 'phaser';
import {
  GAME_WIDTH,
  SPAWN_MARGIN,
  ENEMY,
  ENEMY_CHASE,
  ELITE,
  ELITE_SHOOTER,
  ELITE_BOMBER,
  ELITE_GUARDIAN,
  BOSS,
  BOSS_TYPES,
  COIN,
  DIFFICULTY,
  BULLET_BUFF_FX,
  type BossTypeKey,
  type BulletBuffKind,
} from '../config/GameConfig';
import Enemy from '../entities/Enemy';
import Chest from '../entities/Chest';
import BuffChest from '../entities/BuffChest';
import Coin from '../entities/Coin';
import Difficulty from './Difficulty';
import { MONSTER_KEYS } from './AssetFactory';

interface DropChestOpts {
  x?: number;
  y?: number;
  speed?: number;
}

interface DropCoinOpts {
  vx?: number;
  vy?: number;
}

/**
 * Spawner —— 全屏自由刷怪 / 宝箱（接入 Difficulty 阶梯）
 *
 * 节奏算法：
 *   - 单一计时器：在屏幕顶部任意 x（SPAWN_MARGIN 内距）刷怪
 *   - 每次触发后，下次延迟 = (difficulty.spawnInterval / DENSITY_MUL) × random(1±jitter)
 *   - DENSITY_MUL 越大屏幕越密；2.6 是"自由战场 + 怪物追击"下比较紧张但还能玩的密度
 *
 * 怪物追击：
 *   - 出生瞬间根据玩家相对 x 给一个水平速度分量（initialBiasX）
 *   - 之后每帧由 MainScene 调用 enemy.chaseUpdate(delta, player) 缓慢转向
 *   - Boss / mini Boss 不走追击，沿用各自的 zigzag/直线
 *
 * 公共 API：
 *   update(time, delta)
 *   pause() / resume()
 *   dropChest(opts)          立即下一个宝箱
 *   spawnBoss(type?)         立即在屏幕顶部正中刷一只 Boss
 *   spawnMiniBoss(x, y, parentMaxHp)  splitter Boss 死亡分裂出来的 mini
 *   dropCoin(x, y, opts)     在 (x,y) 处生成一枚金币
 */
export default class Spawner {
  scene: Phaser.Scene;
  enemyGroup: Phaser.Physics.Arcade.Group;
  chestGroup: Phaser.Physics.Arcade.Group;
  /** Buff 箱专用 group（fire/ice/chain）—— 与普通 chestGroup 分开，避免触发升级阈值 */
  bulletBuffChestGroup: Phaser.Physics.Arcade.Group | null = null;
  coinGroup: Phaser.Physics.Arcade.Group;
  difficulty: Difficulty;
  /** 玩家引用 —— 出生时根据玩家位置给水平偏向；可选（dropChest 等不需要） */
  private _player: { x: number; y: number } | null;

  private _paused = false;
  /** Boss 轮换计数（每次 _pickBossType 后 ++） */
  private _bossIdx = -1;

  /** 单一计时器：累计 dt + 下次触发延迟 */
  private _spawnAcc = 0;
  private _nextDelay: number;

  /** 单计时器密度倍率：值越大刷得越密。
   *  2.6 = 比双 lane 时代更密，配合追击玩法营造"四面包夹"压迫感。 */
  private static readonly DENSITY_MUL = 2.6;

  constructor(
    scene: Phaser.Scene,
    enemyGroup: Phaser.Physics.Arcade.Group,
    chestGroup: Phaser.Physics.Arcade.Group,
    coinGroup: Phaser.Physics.Arcade.Group,
    difficulty: Difficulty,
    player?: { x: number; y: number },
    bulletBuffChestGroup?: Phaser.Physics.Arcade.Group,
  ) {
    this.scene = scene;
    this.enemyGroup = enemyGroup;
    this.chestGroup = chestGroup;
    this.coinGroup = coinGroup;
    this.difficulty = difficulty;
    this._player = player ?? null;
    this.bulletBuffChestGroup = bulletBuffChestGroup ?? null;

    this._nextDelay = this._rollNextDelay(1.2); // 开局留 1.2 倍间隔，让玩家先适应位置
  }

  /** 屏内活怪上限 —— 超过这个数就跳过本次生成，防止后期堆叠卡屏 */
  private static SCREEN_ENEMY_CAP = 28;

  update(_time: number, delta: number): void {
    if (this._paused) return;
    // Boss 在场期间停刷常规怪
    if (this.hasActiveBoss()) return;
    this._spawnAcc += delta;
    while (this._spawnAcc >= this._nextDelay) {
      this._spawnAcc -= this._nextDelay;
      // 屏内已有上限：跳过这次生成，但消耗了 _spawnAcc —— 算"压力释放"，
      // 玩家清掉一波后会迎来"急刷"反弹，自然形成潮汐节奏
      if (this._countActiveEnemies() < Spawner.SCREEN_ENEMY_CAP) {
        this._spawnRandom();
      }
      this._nextDelay = this._rollNextDelay();
    }
  }

  /** 屏内活跃（未死）非 Boss 怪计数 —— Boss 战时不会走 update，所以这里只数常规怪 */
  private _countActiveEnemies(): number {
    let n = 0;
    const arr = this.enemyGroup.getChildren();
    for (let i = 0; i < arr.length; i++) {
      const e = arr[i] as Enemy;
      if (e.active && !e._dead && !e.isBoss) n++;
    }
    return n;
  }

  /** 当前是否还有 Boss / mini Boss 在场 */
  hasActiveBoss(): boolean {
    const arr = this.enemyGroup.getChildren();
    for (let i = 0; i < arr.length; i++) {
      const e = arr[i] as Enemy;
      if (e.active && !e._dead && e.isBoss) return true;
    }
    return false;
  }

  /** 外部（天气 / 路线）注入的刷怪间隔倍率（< 1 = 更密） */
  rateMul = 1;

  /**
   * 从 difficulty.spawnInterval 出发滚一个带抖动的下次延迟。
   * @param scale 额外缩放（开局起步用）
   */
  private _rollNextDelay(scale = 1): number {
    // 单计时器要比双 lane 快，所以除以 DENSITY_MUL
    const base = (this.difficulty.spawnInterval / Spawner.DENSITY_MUL) * scale * this.rateMul;
    const j = DIFFICULTY.intervalJitter;
    const factor = 1 + (Math.random() * 2 - 1) * j;  // [1-j, 1+j]
    return Math.max(120, Math.round(base * factor));
  }

  /**
   * 计算出生初速度：vy 为主，vx 朝玩家略偏。
   * 总速模长 = speed；vx = sin(theta) × speed，theta 由 ENEMY_CHASE.initialBiasX 限制
   * 没有 player 引用时退化为纯垂直
   */
  private _initialVelocityToward(spawnX: number, speed: number): { vx: number; vy: number } {
    if (!this._player) return { vx: 0, vy: speed };
    const dx = this._player.x - spawnX;
    // dx 归一化到 [-1, 1]：以半屏宽为基准
    const norm = Phaser.Math.Clamp(dx / (GAME_WIDTH * 0.5), -1, 1);
    // bias 把 norm 映射到水平速度比例 [-bias, bias]
    const horizRatio = norm * ENEMY_CHASE.initialBiasX;
    // 保证 vy 仍为正、模长 = speed
    const vx = horizRatio * speed;
    const vy = Math.sqrt(Math.max(0, speed * speed - vx * vx));
    return { vx, vy };
  }

  /** 在屏幕顶部随机 x 处刷一个怪/宝箱 */
  private _spawnRandom(): void {
    const x = Phaser.Math.Between(SPAWN_MARGIN, GAME_WIDTH - SPAWN_MARGIN);
    const y = -ENEMY.height;
    const speed = this.difficulty.fallSpeed;

    // 普通宝箱（金）—— 进度宝箱
    if (Math.random() < this.difficulty.chestRate) {
      this.dropChest({ x, y, speed });
      return;
    }

    // 元素 buff 箱（fire/ice/chain）—— 独立 roll，达到 minTier 之后才出
    if (
      this.bulletBuffChestGroup &&
      this.difficulty.tier >= BULLET_BUFF_FX.minTier &&
      Math.random() < BULLET_BUFF_FX.spawnRate
    ) {
      const kinds: BulletBuffKind[] = ['fire', 'ice', 'chain'];
      const kind = kinds[Phaser.Math.Between(0, kinds.length - 1)];
      this.dropBulletBuffChest({ x, y, speed, kind });
      return;
    }

    // 射手精英概率（达到 ELITE_SHOOTER.minTier 之后才有，先 roll 它，再 roll 普通精英）
    const isShooter =
      this.difficulty.tier >= ELITE_SHOOTER.minTier &&
      Math.random() < ELITE_SHOOTER.spawnRate;

    // 自爆精英概率（与 shooter 互斥）
    const isBomber =
      !isShooter &&
      this.difficulty.tier >= ELITE_BOMBER.minTier &&
      Math.random() < ELITE_BOMBER.spawnRate;

    // 护盾兵概率（与 shooter/bomber 互斥）
    const isGuardian =
      !isShooter && !isBomber &&
      this.difficulty.tier >= ELITE_GUARDIAN.minTier &&
      Math.random() < ELITE_GUARDIAN.spawnRate;

    // 精英怪概率（达到 ELITE.minTier 之后才有）
    const isElite =
      !isShooter && !isBomber && !isGuardian &&
      this.difficulty.tier >= ELITE.minTier &&
      Math.random() < ELITE.spawnRate;

    const textureKey = MONSTER_KEYS[Phaser.Math.Between(0, MONSTER_KEYS.length - 1)];

    if (isShooter) {
      // 射手精英：HP/体型与普通小怪一致，仅在着色 + 行为上区分
      const hp = Math.round(this.difficulty.enemyHp * ELITE_SHOOTER.hpMul);
      const w = Math.round(ENEMY.width * ELITE_SHOOTER.sizeMul);
      const h = Math.round(ENEMY.height * ELITE_SHOOTER.sizeMul);
      const sSpeed = speed * ELITE_SHOOTER.speedMul;
      const enemy = new Enemy(this.scene, x, y, {
        hp,
        width: w,
        height: h,
        score: Math.round(ENEMY.score * ELITE_SHOOTER.scoreMul),
        textureKey,
        isShooter: true,
      });
      this.enemyGroup.add(enemy);
      const v = this._initialVelocityToward(x, sSpeed);
      enemy.body.setVelocity(v.vx, v.vy);
      enemy.body.setAllowGravity(false);
      return;
    }

    if (isBomber) {
      const hp = Math.round(this.difficulty.enemyHp * ELITE_BOMBER.hpMul);
      const w = Math.round(ENEMY.width * ELITE_BOMBER.sizeMul);
      const h = Math.round(ENEMY.height * ELITE_BOMBER.sizeMul);
      const bSpeed = speed * ELITE_BOMBER.speedMul;
      const enemy = new Enemy(this.scene, x, y, {
        hp,
        width: w,
        height: h,
        score: Math.round(ENEMY.score * ELITE_BOMBER.scoreMul),
        textureKey,
        isBomber: true,
      });
      this.enemyGroup.add(enemy);
      const v = this._initialVelocityToward(x, bSpeed);
      enemy.body.setVelocity(v.vx, v.vy);
      enemy.body.setAllowGravity(false);
      return;
    }

    if (isGuardian) {
      const hp = Math.round(this.difficulty.enemyHp * ELITE_GUARDIAN.hpMul);
      const w = Math.round(ENEMY.width * ELITE_GUARDIAN.sizeMul);
      const h = Math.round(ENEMY.height * ELITE_GUARDIAN.sizeMul);
      const gSpeed = speed * ELITE_GUARDIAN.speedMul;
      const enemy = new Enemy(this.scene, x, y, {
        hp,
        width: w,
        height: h,
        score: Math.round(ENEMY.score * ELITE_GUARDIAN.scoreMul),
        textureKey,
        isGuardian: true,
      });
      this.enemyGroup.add(enemy);
      const v = this._initialVelocityToward(x, gSpeed);
      enemy.body.setVelocity(v.vx, v.vy);
      enemy.body.setAllowGravity(false);
      return;
    }

    if (isElite) {
      const hp = Math.round(this.difficulty.enemyHp * ELITE.hpMul);
      const w = Math.round(ENEMY.width * ELITE.sizeMul);
      const h = Math.round(ENEMY.height * ELITE.sizeMul);
      const eliteSpeed = speed * ELITE.speedMul;
      const enemy = new Enemy(this.scene, x, y, {
        hp,
        width: w,
        height: h,
        score: Math.round(ENEMY.score * ELITE.scoreMul),
        textureKey,
        isElite: true,
      });
      this.enemyGroup.add(enemy);
      const v = this._initialVelocityToward(x, eliteSpeed);
      enemy.body.setVelocity(v.vx, v.vy);
      enemy.body.setAllowGravity(false);
      return;
    }

    const hp = this.difficulty.enemyHp;
    const enemy = new Enemy(this.scene, x, y, { hp, textureKey });
    this.enemyGroup.add(enemy);
    const v = this._initialVelocityToward(x, speed);
    enemy.body.setVelocity(v.vx, v.vy);
    enemy.body.setAllowGravity(false);
  }

  dropChest(opts: DropChestOpts = {}): Chest {
    const x = opts.x ?? Phaser.Math.Between(SPAWN_MARGIN, GAME_WIDTH - SPAWN_MARGIN);
    const y = opts.y ?? -ENEMY.height;
    const speed = opts.speed ?? this.difficulty.fallSpeed;
    const chest = new Chest(this.scene, x, y);
    this.chestGroup.add(chest);
    chest.body.setVelocity(0, speed);
    chest.body.setAllowGravity(false);
    return chest;
  }

  /**
   * 刷一个元素 buff 箱（fire / ice / chain）—— 独立于普通金宝箱。
   * 玩家撞到立即获得 8s 元素弹药，不计入升级阈值。
   * 调用方必须先把 bulletBuffChestGroup 注入到 Spawner（构造时第 7 参），
   * 否则 _spawnRandom 不会触发，dropBulletBuffChest 直接 throw。
   */
  dropBulletBuffChest(opts: DropChestOpts & { kind: BulletBuffKind }): BuffChest {
    if (!this.bulletBuffChestGroup) {
      throw new Error('Spawner.dropBulletBuffChest: bulletBuffChestGroup not injected');
    }
    const x = opts.x ?? Phaser.Math.Between(SPAWN_MARGIN, GAME_WIDTH - SPAWN_MARGIN);
    const y = opts.y ?? -ENEMY.height;
    const speed = opts.speed ?? this.difficulty.fallSpeed;
    const chest = new BuffChest(this.scene, x, y, opts.kind);
    this.bulletBuffChestGroup.add(chest);
    chest.body.setVelocity(0, speed);
    chest.body.setAllowGravity(false);
    return chest;
  }

  /**
   * 立即刷一只 Boss。
   * @param type 'standard' | 'zigzag' | 'splitter'；不传则按内部轮换顺序选下一个
   */
  spawnBoss(type?: BossTypeKey): Enemy {
    const key: BossTypeKey = type && BOSS_TYPES[type] ? type : this._pickBossType();
    const cfg = BOSS_TYPES[key];

    const x = GAME_WIDTH / 2;
    const y = -BOSS.height;
    const baseHp = this.difficulty.enemyHp * DIFFICULTY.bossHpMul;
    const hp = Math.round(baseHp * cfg.hpMul);
    const speed = Math.round(this.difficulty.fallSpeed * 0.55);
    const w = Math.round(BOSS.width * cfg.sizeMul);
    const h = Math.round(BOSS.height * cfg.sizeMul);

    const boss = new Enemy(this.scene, x, y, {
      hp,
      width: w,
      height: h,
      score: BOSS.scoreReward,
      isBoss: true,
      bossType: key,
    });
    this.enemyGroup.add(boss);
    boss.body.setVelocity(0, speed);
    boss.body.setAllowGravity(false);
    return boss;
  }

  /** splitter 死亡分裂的 mini Boss */
  spawnMiniBoss(x: number, y: number, parentMaxHp: number): Enemy {
    const cfg = BOSS_TYPES.splitter;
    const ratio = cfg.splitMiniHpRatio ?? 0.35;
    const sizeMul = cfg.splitMiniSizeMul ?? 0.55;
    const hp = Math.max(1, Math.round(parentMaxHp * ratio));
    const speed = this.difficulty.fallSpeed * 0.7;
    const w = Math.round(BOSS.width * sizeMul);
    const h = Math.round(BOSS.height * sizeMul);

    const mini = new Enemy(this.scene, x, y, {
      hp,
      width: w,
      height: h,
      score: Math.round(BOSS.scoreReward * 0.4),
      isBoss: true,
      isMiniBoss: true,
      bossType: 'splitter',
    });
    this.enemyGroup.add(mini);
    mini.body.setVelocity(0, speed);
    mini.body.setAllowGravity(false);
    return mini;
  }

  private _pickBossType(): BossTypeKey {
    const order: BossTypeKey[] = ['standard', 'zigzag', 'splitter'];
    this._bossIdx += 1;
    return order[this._bossIdx % order.length];
  }

  dropCoin(x: number, y: number, opts: DropCoinOpts = {}): Coin {
    const c = new Coin(this.scene, x, y);
    this.coinGroup.add(c);
    const vx = opts.vx ?? 0;
    const vy = opts.vy ?? COIN.speed;
    c.body.setVelocity(vx, vy);
    c.body.setAllowGravity(false);
    return c;
  }

  pause(): void { this._paused = true; }
  resume(): void { this._paused = false; }
}
